import { useEffect, useMemo, useState } from 'react';

import { useOutletContext, useRouteLoaderData } from '@remix-run/react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { usePostPageContext } from '~/context/usePostPageContext';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { RootLoaderData } from '~/root';
import type { PostLoaderData } from '~/routes/p/$slug';

import useClipContentHeight from './useClipContentHeight';
import useIsLoggedIn from './useIsLoggedIn';
import useScrollPosition from './useScrollPosition';
import useTypedLoaderData from './useUtils/useTypedLoaderData';

const PIXELS_SCROLLED_BEFORE_SHOWING_GATE = 50;
const PERCENTAGE_SCROLLED_BEFORE_SHOWING_MODAL = 40;

export const usePostPageScrollActions = ({
  showAfterPercentageScrolled = PERCENTAGE_SCROLLED_BEFORE_SHOWING_MODAL,
}: {
  showAfterPercentageScrolled?: number;
} = {}) => {
  const { isModalOpen } = useGlobalModals();
  const { post } = useTypedLoaderData<PostLoaderData>();
  const { isGatedContentDisabled, isPopupDisabled } = usePostPageContext() || {};

  const { percentageScrolled, windowScrollY } = useScrollPosition() || {
    percentageScrolled: 0,
    windowScrollY: 0,
  };
  const currentProgress = Number((percentageScrolled * 100).toFixed(1));
  const isLoggedIn = useIsLoggedIn();
  const { isFetchingSubscription } = useRouteLoaderData('root') as RootLoaderData;
  const { setCookie } = useOutletContext<OutletContext>();
  const [isOpen, setIsOpen] = useState(false);
  const [isGateOpen, setIsGateOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const isGated = useMemo(() => {
    if (isFetchingSubscription) return false;

    return (
      post.enforce_gated_content &&
      !post.contains_subscriber_break &&
      !isGatedContentDisabled &&
      !isPopupDisabled &&
      !isLoggedIn
    );
  }, [post, isGatedContentDisabled, isPopupDisabled, isLoggedIn, isFetchingSubscription]);

  const getElementHeights = () => {
    let contentBounds: any;
    let windowHeight: any;

    if (typeof document !== 'undefined') {
      contentBounds = document.querySelector('.rendered-post')?.getBoundingClientRect();
      windowHeight = window?.innerHeight;
    }

    return { contentBounds, windowHeight };
  };

  const showPopup = useMemo(() => {
    if (isFetchingSubscription) return false;

    return (
      post.enable_popup_on_scroll &&
      currentProgress >= showAfterPercentageScrolled &&
      !isGatedContentDisabled &&
      !isPopupDisabled &&
      !isLoggedIn
    );
  }, [
    currentProgress,
    isGatedContentDisabled,
    isLoggedIn,
    isPopupDisabled,
    post.enable_popup_on_scroll,
    showAfterPercentageScrolled,
    isFetchingSubscription,
  ]);

  const showSubscriberGate = mounted && isGated && windowScrollY >= PIXELS_SCROLLED_BEFORE_SHOWING_GATE;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Ensure popup closes
  useEffect(() => {
    if (isPopupDisabled && isOpen) {
      setIsOpen(false);
    }
  }, [isPopupDisabled, isOpen]);

  // Ensure gate closes
  useEffect(() => {
    if (isGatedContentDisabled && isGateOpen) {
      setIsGateOpen(false);
    }
  }, [isGatedContentDisabled, isGateOpen]);

  // Handle logic around when popup is enabled
  useEffect(() => {
    if (showPopup) {
      setIsOpen(true);
    }
  }, [showPopup]);

  // Handle logic around when gate is enabled
  useEffect(() => {
    setIsGateOpen(showSubscriberGate);
  }, [windowScrollY, showSubscriberGate]);

  useEffect(() => {
    if (!mounted || !isGated) return;

    const { contentBounds, windowHeight } = getElementHeights();

    if (
      (contentBounds &&
        windowHeight &&
        contentBounds.height + windowScrollY < windowHeight - PIXELS_SCROLLED_BEFORE_SHOWING_GATE) ||
      showSubscriberGate
    ) {
      setIsGateOpen(true);
    }
  }, [mounted, isGated, showSubscriberGate, windowScrollY]);

  useClipContentHeight({
    enabled: mounted && isGated && !isModalOpen,
  });

  const handleCloseModal = () => {
    setCookie({ name: 'popup_closed', value: 'true', days: 4 });
    setIsOpen(false);
  };

  return {
    mounted,
    isGated,
    isOpen,
    isGateOpen,
    showPopup,
    handleCloseModal,
    currentProgress,
  };
};
